import RefreshIcon from "@assets/images/refresh-primary.svg";
import SearchIconNew from "@assets/images/Search-new.svg";
import { Box, Button, Grid } from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  AlterMaterialFilterInput,
  useSearchSuggestionDomesticSoldTosLazyQuery,
  useSuggestionSearchMaterialLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { useFormik } from "formik";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import InputSuggestion from "../InputSuggestion";

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: "8px",
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      height: "260px"
    },
    header: {
      padding: "8px 0px 8px 32px",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      flexDirection: "column",
      "& .MuiTypography-root": {
        color: "white"
      },
      "& .MuiTypography-caption": {
        fontSize: "1rem",
        lineHeight: "1.6rem"
      },
      "& .MuiTypography-h6": {
        lineHeight: "2.4rem"
      }
    },
    headerText: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px"
    },
    headerTextBold: {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px"
    },
    searchBox: {
      padding: "12px 24px"
    },
    negativeMargin: {
      margin: "-1.2rem -0.6rem !important"
    },
    gridPadding: {
      padding: ".6rem !important"
    },
    btnContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0px 12px 0px",
      margin: " 0px 24px 0px 24px "
    },
    errorMessage: {
      color: theme.palette.error.light,
      lineHeight: "18px",
      marginTop: 4
    },
    successMessage: {
      color: theme.palette.success.main
    },
    info: {
      display: "flex",
      alignItems: "center",

      "& >img": {
        marginRight: 10
      }
    },
    btn: {
      minWidth: 132,
      maxHeight: 40,
      marginLeft: 16,
      padding: "8px 26.5px",
      "& .MuiTypography-root": {
        color: "inherit"
      }
    },
    btnTxt: {
      lineHeight: "22px",
      fontSize: "14px"
    },
    gradeWrapper: {
      paddingTop: `24px`
    },
    label: {
      marginBottom: "4px"
    },
    button: {
      display: "flex",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        "& button": {
          margin: "10px 0"
        }
      }
    }
  }),
  { name: "AlternatedMaterialFilter" }
);

interface AlternatedMaterialFilterProps {
  handleSearch: (value: AlterMaterialFilterInput) => void;
  message: { icon: string; message: string; color: string };
  onReset?: () => void;
  isSuccess?: boolean;
  setIsSuccess?: Dispatch<SetStateAction<boolean>>;
}

const AlternatedMaterialFilter: React.FC<AlternatedMaterialFilterProps> = ({
  handleSearch,
  onReset,
  message,
  isSuccess,
  setIsSuccess
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [reset, setReset] = useState<boolean>(true);

  const [
    getDataMaterial,
    { data: fetchDataMaterial, loading: loadingDataMaterial }
  ] = useSuggestionSearchMaterialLazyQuery();

  const [
    getDataSoldTo,
    { data: fetchDataSoldTo, loading: loadingDataSoldTo }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery();

  const formik = useFormik({
    initialValues: {
      soldTo: "",
      material: "",
      grade: "",
      gram: ""
    },
    onSubmit: () => {
      //
    }
  });

  const dataSoldTo = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSoldTo?.searchSuggestionDomesticSoldTos) || [];
    return data.map(data => ({
      title: `${data.code} - ${data.name}`,
      value: data.code
    }));
  }, [fetchDataSoldTo?.searchSuggestionDomesticSoldTos]);

  const dataMaterials = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataMaterial?.suggestionSearchMaterial) || [];
    return data.map(data => ({
      title: `${data.code} `,
      value: data.code
    }));
  }, [fetchDataMaterial?.suggestionSearchMaterial]);

  const handleResetForm = () => {
    formik.resetForm();
    setReset(prev => !prev);
    onReset();
  };

  useEffect(() => {
    if (isSuccess) {
      handleResetForm();
      setIsSuccess(false);
    }
  }, [isSuccess]);
  const handleChangeSoldTo = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSoldTo({
        variables: { first: 100, filter: { search: value } }
      });
    }
  };

  const handleSearchAlternated = () => {
    const { material, gram, grade, soldTo } = formik.values;
    handleSearch({ materialCode: material, soldTo, grade, gram });
  };

  const handleSetMaterial = selected => {
    if (selected?.value) {
      formik.setFieldValue("material", selected.value);
      return;
    }
  };

  return (
    <Box className={classes.root}>
      <Grid className={classes.header} container>
        <MyTypography variant="subtitle1" className={classes.headerText}>
          <span className={classes.headerTextBold}>Search :</span> Master -
          Alternated Material
        </MyTypography>
      </Grid>
      <Grid className={classes.searchBox} container>
        <Grid container spacing={3} className={classes.negativeMargin}>
          <Grid item xs={12} className={classes.gridPadding}>
            <MyTypography variant="subtitle3" className={classes.label}>
              <FormattedMessage defaultMessage="Sold-to Party" />
            </MyTypography>
            <Autocomplete
              resetAlways
              optionsProp={dataSoldTo}
              placeholderProps={intl.formatMessage({
                defaultMessage: "ค้นหา  Sold-to Party",
                description: "label"
              })}
              onChangeProps={handleChangeSoldTo}
              onSearch={value => formik.setFieldValue("soldTo", value)}
              isReset={reset}
              loading={loadingDataSoldTo}
              id="soldTo"
              name="soldTo"
              testId="soldTo"
            />
          </Grid>
          <Grid item xs={6} className={classes.gridPadding}>
            <MyTypography variant="subtitle3" className={classes.label}>
              <FormattedMessage defaultMessage="Material Input" />
            </MyTypography>
            <InputSuggestion
              searchField="search"
              dataOptions={dataMaterials}
              loadingDataSuggestion={loadingDataMaterial}
              objectSuggestion
              getDataSuggestions={getDataMaterial}
              contained
              name={"materialCode"}
              isCheckingRole
              placeholder={"ค้นหา Material Input"}
              handleSelectedSearch={handleSetMaterial}
              isClean={reset}
              id="materialCode"
              testId="materialCode"
            />
          </Grid>
          <Grid item xs={3} className={classes.gridPadding}>
            <MyTypography variant="subtitle3" className={classes.label}>
              <FormattedMessage defaultMessage="Grade" />
            </MyTypography>
            <SingleTextField
              value={formik.values.grade}
              name="grade"
              onChange={formik.handleChange}
              placeholder="ระบุ Grade"
              id="grade"
              InputProps={{
                inputProps: { "data-testid": "grade-input" }
              }}
            />
          </Grid>
          <Grid item xs={3} className={classes.gridPadding}>
            <MyTypography variant="subtitle3" className={classes.label}>
              <FormattedMessage defaultMessage="Gram" />
            </MyTypography>
            <SingleTextField
              value={formik.values.gram}
              name="gram"
              onChange={formik.handleChange}
              placeholder="ระบุ Gram"
              id="gram"
              InputProps={{
                inputProps: { "data-testid": "gram-input" }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.btnContainer}>
        <div className={classes.info}>
          <img src={message.icon} alt="" />
          <MyTypography variant="body3" style={{ color: message.color }}>
            {message.message}
          </MyTypography>
        </div>
        <div className={classes.button}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetForm}
            className={classes.btn}
            id="resetButton"
            name="resetButton"
            data-testid="resetButton"
          >
            <SVG src={RefreshIcon} width={24} height={24} />
            <MyTypography variant="body3" className={classes.btnTxt}>
              <FormattedMessage defaultMessage="ล้างข้อมูล" />
            </MyTypography>
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className={classes.btn}
            onClick={handleSearchAlternated}
            disabled={!formik.dirty}
            id="searchButton"
            name="searchButton"
            data-testid="searchButton"
          >
            <SVG src={SearchIconNew} width={24} height={24} />
            <MyTypography
              variant="button2"
              color="inherit"
              className={classes.btnTxt}
            >
              <FormattedMessage defaultMessage="ค้นหา" />
            </MyTypography>
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default AlternatedMaterialFilter;
